import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import {
    DossierOpenSearch,
    DossierOpenSearchSelectorMain,
    DossierFormaliteApiObject,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { BoxSkeleton, HeaderSkeleton, SkeletonLoader } from '@europrocurement/flexy-components';
import SelectedDossierContext from './SelectedDossierContext';
import { UpdateDossierPayload } from '../types/UpdateDossierPayload';
import useDossierSiblings from '../hooks/useDossierSiblings';
import useDossier from '../hooks/useDossier';

const SelectedDossierProvider: React.FunctionComponent<PropsWithChildren> = function ({
    children,
}) {
    const [selectedDossier, setSelectedDossier] = useState<DossierOpenSearch>({
        formalites: [],
        validationProcesses: [],
    });
    const [collectionId, setCollectionId] = useState<number>();
    const [siblings, setSiblings] = useState<Array<DossierOpenSearch>>([]);

    const [dossierWithNotes, setDossierWithNotes] = useState<DossierFormaliteApiObject>({});
    const [notesLoading, setNotesLoading] = useState(false);
    const [notesError, setNotesError] = useState<string | null>(null);

    const dispatch = useDispatch();
    const dossierDataSource: DataSource<DossierOpenSearch> = useSelector(
        DossierOpenSearchSelectorMain,
    );
    const { idDossier } = useParams();
    const { selectOpenSearchDossier, dossierApi } = useFormaliteService();
    const { getDossierSiblings } = useDossierSiblings();
    const { updateDossier: patchDossier } = useDossier();

    const updateDossier = useCallback(
        (payload: UpdateDossierPayload) => {
            setSelectedDossier({
                ...selectedDossier,
                ...payload,
            });
            patchDossier(selectedDossier.id as number, payload);
        },
        [selectedDossier, patchDossier],
    );

    const refreshDossier = useCallback(() => {
        dispatch(
            selectOpenSearchDossier({
                id: idDossier as unknown as number,
            }) as unknown as AnyAction,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshDossierWithNotes = useCallback(async () => {
        if (!idDossier) {
            console.warn('Aucun ID de dossier disponible pour refreshDossierWithNotes');
            return;
        }

        setNotesLoading(true);
        setNotesError(null);

        try {
            const response = await dossierApi.apiDossiersIdGet({
                id: idDossier.toString(),
            });

            setDossierWithNotes(response.data || {});
        } catch (err) {
            console.error('Erreur lors du chargement des notes:', err);
            setNotesError('Erreur lors du chargement des notes du dossier');
        } finally {
            setNotesLoading(false);
        }
    }, [idDossier, dossierApi]);

    useEffect(() => {
        if (!dossierDataSource?.selected || dossierDataSource?.selected?.id !== idDossier) {
            dispatch(
                selectOpenSearchDossier({
                    id: idDossier as unknown as number,
                }) as unknown as AnyAction,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idDossier]);

    useEffect(() => {
        if (dossierDataSource.selectedStatus === 'succeeded') {
            setSelectedDossier(dossierDataSource.selected as DossierOpenSearch);
            setCollectionId(dossierDataSource.selected?.collectionId as number);
        }

        return () => {
            setCollectionId(undefined);
        };
    }, [dossierDataSource]);

    useEffect(() => {
        if (collectionId) {
            getDossierSiblings(collectionId).then(setSiblings);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionId]);

    useEffect(() => {
        if (dossierDataSource.selectedStatus === 'succeeded' && idDossier) {
            // Charger les notes seulement si on n'a pas déjà les bonnes données
            if (!dossierWithNotes.id || dossierWithNotes.id?.toString() !== idDossier) {
                refreshDossierWithNotes();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossierDataSource.selectedStatus, idDossier]);

    const contextValue = useMemo(
        () => ({
            dossier: selectedDossier,
            setDossier: setSelectedDossier,
            siblings,
            setSiblings,
            updateDossier,
            refreshDossier,
            dossierWithNotes,
            setDossierWithNotes,
            refreshDossierWithNotes,
            notesLoading,
            notesError,
        }),
        [
            selectedDossier,
            setSelectedDossier,
            siblings,
            setSiblings,
            updateDossier,
            refreshDossier,
            dossierWithNotes,
            setDossierWithNotes,
            refreshDossierWithNotes,
            notesLoading,
            notesError,
        ],
    );

    return (
        <SkeletonLoader
            isLoading={
                dossierDataSource.selectedStatus !== 'succeeded' &&
                (dossierDataSource.selectedStatus === 'loading' ||
                    dossierDataSource.selectedStatus === 'idle')
            }
            isInError={
                dossierDataSource.selectedStatus !== 'succeeded' &&
                dossierDataSource.selectedStatus !== 'loading' &&
                dossierDataSource.selectedStatus !== 'idle'
            }
            skeleton={
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '80%',
                            width: '80%',
                            gap: 2,
                        }}
                    >
                        <HeaderSkeleton
                            withToolbar
                            headerHeight="120px"
                            contentWidth="100%"
                        />
                        <BoxSkeleton
                            sx={{
                                height: '1200px',
                                width: '100%',
                            }}
                        />
                    </Box>
                </Box>
            }
        >
            <SelectedDossierContext.Provider value={contextValue}>
                {children}
            </SelectedDossierContext.Provider>
        </SkeletonLoader>
    );
};

export default SelectedDossierProvider;
